import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { FC, default as React, memo } from 'react';
import { useAppSelector } from '../../app/hooks';
import { AgentStatuses } from '../../redux/features/taskRouter/taskRouter.constants';
import { selectAgentStatus } from '../../redux/features/taskRouter/taskRouter.selector';
import TaskRouterAgentAssignment from '../TaskRouterAgentAssignment';
import TaskRouterAgentAvailable from '../TaskRouterAgentAvailable';
import TaskRouterAgentTraining from '../TaskRouterAgentTraining';
import TaskRouterAgentUnavailable from '../TaskRouterAgentUnavailable';

const useStyles = makeStyles<Theme>(({ spacing }) => ({
  taskRouterBodyClass: {
    height: `calc(100vh - ${spacing(8)})`,
    '& >div': {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
    },
  },
}));

type TaskRouterBodyProps = {
  handleAgentAvailability: (agentStatus: AgentStatuses) => void;
  isTaskAssignmentMode: boolean;
  sendTaskMessage?: Function;
  /**
   * HITL 8.0.0: hidden, never unmounted, while the lobby grid holds an assist
   * card — `TaskRouterAgentAssignment` under it plays the task-assigned sound.
   * Unset, no `style` is rendered: the body is laid out exactly as before.
   */
  hidden?: boolean;
};

const TaskRouterBody: FC<TaskRouterBodyProps> = ({
  handleAgentAvailability,
  isTaskAssignmentMode,
  sendTaskMessage,
  hidden = false,
}) => {
  const { taskRouterBodyClass } = useStyles();
  const agentStatus = useAppSelector(selectAgentStatus);

  return (
    <div
      data-testid="task-router-body"
      className={taskRouterBodyClass}
      style={hidden ? { display: 'none' } : undefined}
    >
      {(() => {
        switch (agentStatus) {
          case AgentStatuses.available:
            if (isTaskAssignmentMode) return <TaskRouterAgentAssignment />;
            return (
              <TaskRouterAgentAvailable sendTaskMessage={sendTaskMessage} />
            );
          case AgentStatuses.unavailable:
            return (
              <TaskRouterAgentUnavailable
                handleAgentAvailability={handleAgentAvailability}
              />
            );
          case AgentStatuses.training:
            return (
              <TaskRouterAgentTraining
                handleAgentAvailability={handleAgentAvailability}
              />
            );
          default:
            return null;
        }
      })()}
    </div>
  );
};

export default memo(TaskRouterBody);
